import Phaser from 'phaser';
import { TILE_WIDTH, TILE_HEIGHT, EquipmentType } from '@/config';
import { colorToHex } from '@/utils/ColorUtils';

/** Generate an isometric equipment sprite as a procedural texture */
export function generateEquipmentTextures(scene: Phaser.Scene): void {
  generateDJBooth(scene);
  generateSpeaker(scene);
  generateLaser(scene);
  generateStrobe(scene);
  generateFogMachine(scene);
  generateBar(scene);
  generateDanceFloorTile(scene);
}

function generateDJBooth(scene: Phaser.Scene): void {
  const key = 'equipment-DJ_BOOTH';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 24;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  const cx = w / 2;
  const tableH = 12; // height of the 3D extrusion

  // Top face of DJ table (isometric diamond)
  const topY = 4;
  ctx.fillStyle = '#1a0a2a';
  ctx.beginPath();
  ctx.moveTo(cx, topY);
  ctx.lineTo(w - 6, topY + 14);
  ctx.lineTo(cx, topY + 28);
  ctx.lineTo(6, topY + 14);
  ctx.closePath();
  ctx.fill();

  // Right side face (darker for depth)
  ctx.fillStyle = '#120820';
  ctx.beginPath();
  ctx.moveTo(cx, topY + 28);
  ctx.lineTo(w - 6, topY + 14);
  ctx.lineTo(w - 6, topY + 14 + tableH);
  ctx.lineTo(cx, topY + 28 + tableH);
  ctx.closePath();
  ctx.fill();

  // Left side face (medium shade)
  ctx.fillStyle = '#180c28';
  ctx.beginPath();
  ctx.moveTo(cx, topY + 28);
  ctx.lineTo(6, topY + 14);
  ctx.lineTo(6, topY + 14 + tableH);
  ctx.lineTo(cx, topY + 28 + tableH);
  ctx.closePath();
  ctx.fill();

  // Top surface details: turntable platters (ellipses for iso perspective)
  ctx.fillStyle = '#2a1a3a';
  ctx.beginPath();
  ctx.ellipse(cx - 10, topY + 12, 6, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx + 10, topY + 16, 6, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  // Turntable spindles (bright dots)
  ctx.fillStyle = '#ff00ff';
  ctx.beginPath();
  ctx.arc(cx - 10, topY + 12, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx + 10, topY + 16, 2, 0, Math.PI * 2);
  ctx.fill();

  // Mixer strip in the center
  ctx.fillStyle = '#3a1a4a';
  ctx.fillRect(cx - 3, topY + 11, 6, 8);

  // Mixer knobs
  ctx.fillStyle = '#ff44ff';
  ctx.fillRect(cx - 1, topY + 12, 2, 2);
  ctx.fillRect(cx - 1, topY + 16, 2, 2);

  // Front edge LED strip glow
  ctx.shadowColor = '#ff00ff';
  ctx.shadowBlur = 6;
  ctx.strokeStyle = '#ff00ff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(6, topY + 14 + tableH);
  ctx.lineTo(cx, topY + 28 + tableH);
  ctx.lineTo(w - 6, topY + 14 + tableH);
  ctx.stroke();
  ctx.shadowBlur = 0;

  canvas.refresh();
}

function generateSpeaker(scene: Phaser.Scene): void {
  const key = 'equipment-SPEAKER';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 20;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Speaker cabinet - tall isometric box
  const yOff = 4;
  // Front face
  ctx.fillStyle = '#333333';
  ctx.fillRect(w / 2 - 10, yOff, 20, 28);
  // Speaker cone circles
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  ctx.arc(w / 2, yOff + 10, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(w / 2, yOff + 22, 4, 0, Math.PI * 2);
  ctx.fill();
  // Ring highlight
  ctx.strokeStyle = '#555555';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(w / 2, yOff + 10, 6, 0, Math.PI * 2);
  ctx.stroke();

  canvas.refresh();
}

function generateLaser(scene: Phaser.Scene): void {
  const key = 'equipment-LASER';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 16;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Tripod base
  ctx.strokeStyle = '#444444';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(w / 2, 16);
  ctx.lineTo(w / 2 - 10, h - 4);
  ctx.moveTo(w / 2, 16);
  ctx.lineTo(w / 2 + 10, h - 4);
  ctx.moveTo(w / 2, 16);
  ctx.lineTo(w / 2, h - 2);
  ctx.stroke();

  // Laser head
  ctx.fillStyle = '#00ff00';
  ctx.shadowColor = '#00ff00';
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.arc(w / 2, 12, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;

  canvas.refresh();
}

function generateStrobe(scene: Phaser.Scene): void {
  const key = 'equipment-STROBE';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 12;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Mount pole
  ctx.fillStyle = '#444444';
  ctx.fillRect(w / 2 - 2, 8, 4, 24);

  // Strobe light head
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = '#ffffff';
  ctx.shadowBlur = 12;
  ctx.fillRect(w / 2 - 8, 4, 16, 8);
  ctx.shadowBlur = 0;

  canvas.refresh();
}

function generateFogMachine(scene: Phaser.Scene): void {
  const key = 'equipment-FOG_MACHINE';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 12;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Machine body
  ctx.fillStyle = '#3a3a3a';
  ctx.beginPath();
  ctx.moveTo(w / 2 - 12, h - 8);
  ctx.lineTo(w / 2 + 12, h - 8);
  ctx.lineTo(w / 2 + 10, h - 20);
  ctx.lineTo(w / 2 - 10, h - 20);
  ctx.closePath();
  ctx.fill();

  // Nozzle
  ctx.fillStyle = '#666666';
  ctx.fillRect(w / 2 - 3, h - 24, 6, 6);

  // Fog puff indicator
  ctx.fillStyle = 'rgba(200,200,200,0.3)';
  ctx.beginPath();
  ctx.arc(w / 2, h - 30, 6, 0, Math.PI * 2);
  ctx.fill();

  canvas.refresh();
}

function generateBar(scene: Phaser.Scene): void {
  const key = 'equipment-BAR';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT + 16;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Bar counter - isometric box
  const yOff = 8;
  ctx.fillStyle = '#4a2800';
  ctx.beginPath();
  ctx.moveTo(w / 2, yOff);
  ctx.lineTo(w - 6, h / 2 + yOff - 2);
  ctx.lineTo(w / 2, h - 8);
  ctx.lineTo(6, h / 2 + yOff - 2);
  ctx.closePath();
  ctx.fill();

  // Counter top
  ctx.fillStyle = '#6b3a00';
  ctx.beginPath();
  ctx.moveTo(w / 2, yOff - 2);
  ctx.lineTo(w - 4, h / 2 + yOff - 4);
  ctx.lineTo(w / 2, h - 10);
  ctx.lineTo(4, h / 2 + yOff - 4);
  ctx.closePath();
  ctx.fill();

  // Bottles
  ctx.fillStyle = '#ff6600';
  ctx.fillRect(w / 2 - 4, yOff + 2, 3, 6);
  ctx.fillStyle = '#00ffff';
  ctx.fillRect(w / 2 + 1, yOff + 2, 3, 6);

  canvas.refresh();
}

function generateDanceFloorTile(scene: Phaser.Scene): void {
  const key = 'equipment-DANCE_FLOOR';
  if (scene.textures.exists(key)) return;
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT;
  const canvas = scene.textures.createCanvas(key, w, h)!;
  const ctx = canvas.context;

  // Glowing dance floor diamond
  ctx.beginPath();
  ctx.moveTo(w / 2, 1);
  ctx.lineTo(w - 1, h / 2);
  ctx.lineTo(w / 2, h - 1);
  ctx.lineTo(1, h / 2);
  ctx.closePath();

  ctx.fillStyle = '#2a0040';
  ctx.fill();

  // Inner glow pattern
  ctx.beginPath();
  ctx.moveTo(w / 2, 4);
  ctx.lineTo(w - 6, h / 2);
  ctx.lineTo(w / 2, h - 4);
  ctx.lineTo(6, h / 2);
  ctx.closePath();
  ctx.strokeStyle = '#9900ff';
  ctx.lineWidth = 1;
  ctx.stroke();

  canvas.refresh();
}
